import { evaluateEligibility } from "../evaluation/eligibility.js";
import { evaluateFit } from "../evaluation/fit-score.js";
import type { CompanyProfile } from "../evaluation/profile.js";
import type { NoticeEvaluation } from "../notices/types.js";
import type { BrowserDatabase, RemoteNoticeRecord } from "./schema.js";
import { getSetting, putSetting } from "./repository.js";

export const COMPANY_PROFILE_SETTING_KEY = "companyProfile" as const;

/** Content-based local version so snapshot profile versions never collide. */
export function localCompanyProfileVersion(profile: CompanyProfile): string {
  const fingerprint = {
    company: profile.company,
    capabilities: profile.capabilities,
    targetMarkets: profile.targetMarkets,
    regionsOfInterest: profile.regionsOfInterest,
    partnerships: profile.partnerships,
    constraints: profile.constraints,
  };
  const text = JSON.stringify(fingerprint);
  let hash = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `local-${(hash >>> 0).toString(16).padStart(8, "0")}`;
}

export function withLocalProfileVersion(
  profile: CompanyProfile,
  verifiedAt = new Date().toISOString().slice(0, 10),
): CompanyProfile {
  return {
    ...profile,
    version: localCompanyProfileVersion(profile),
    verifiedAt,
  };
}

export async function getStoredCompanyProfile(
  database: BrowserDatabase,
): Promise<CompanyProfile | null> {
  const value = await getSetting<CompanyProfile>(database, COMPANY_PROFILE_SETTING_KEY);
  if (!value || typeof value !== "object") return null;
  if (typeof value.version !== "string" || !value.company) return null;
  return value;
}

export interface ReevaluateResult {
  noticeCount: number;
  profileVersion: string;
  evaluatedAt: string;
}

/**
 * Re-scores every cached notice with the given company profile and persists
 * evaluations plus denormalized notice score fields in one transaction.
 */
export async function reevaluateAllNotices(
  database: BrowserDatabase,
  profileInput: CompanyProfile,
  evaluatedAt = new Date().toISOString(),
): Promise<ReevaluateResult> {
  const profile = withLocalProfileVersion(profileInput, evaluatedAt.slice(0, 10));
  const notices = await database.remoteNotices.toArray();
  const evaluations: NoticeEvaluation[] = notices.map((notice) => {
    const eligibility = evaluateEligibility(notice, profile, evaluatedAt);
    return evaluateFit(notice, profile, eligibility, evaluatedAt);
  });
  const evaluationById = new Map(evaluations.map((evaluation) => [evaluation.noticeId, evaluation]));
  const updatedNotices: RemoteNoticeRecord[] = notices.map((notice) => {
    const evaluation = evaluationById.get(notice.id);
    return {
      ...notice,
      eligibilityStatus: evaluation?.eligibilityStatus ?? null,
      fitScore: evaluation?.fitScore ?? null,
      fitGrade: evaluation?.fitGrade ?? null,
    };
  });

  await database.transaction(
    "rw",
    database.remoteNotices,
    database.remoteEvaluations,
    database.settings,
    async () => {
      await database.remoteEvaluations.clear();
      await database.remoteEvaluations.bulkPut(evaluations);
      await database.remoteNotices.bulkPut(updatedNotices);
      await putSetting(database, COMPANY_PROFILE_SETTING_KEY, profile, evaluatedAt);
    },
  );

  return {
    noticeCount: notices.length,
    profileVersion: profile.version,
    evaluatedAt,
  };
}

/** After remote sync overwrites evaluations, re-apply a saved local profile if present. */
export async function reapplyStoredCompanyProfile(
  database: BrowserDatabase,
  evaluatedAt = new Date().toISOString(),
): Promise<ReevaluateResult | null> {
  const stored = await getStoredCompanyProfile(database);
  if (!stored) return null;
  return reevaluateAllNotices(database, stored, evaluatedAt);
}
